import { DynamicConfig } from './dynamicConfig.js';
import { JsonObject, JsonValue, JsonValueType } from '../app/json/jsonValue.js';

export class DynamicConfigMap {
    #values: Map<string, DynamicConfig[]> = new Map();

    set(key: string, value: DynamicConfig[]): void {
        this.#values.set(this.findKeyCaseInsensitive(key) ?? key, value);
    }

    get(key: string): DynamicConfig[] | null {
        return this.#values.get(this.findKeyCaseInsensitive(key) ?? key) || null;
    }

    tryGetValue(key: string): { found: boolean; value: DynamicConfig[] | null } {
        const value = this.get(key);
        return { found: value !== null, value };
    }

    has(key: string): boolean {
        return this.#values.has(this.findKeyCaseInsensitive(key) ?? key);
    }

    containsKey(key: string): boolean {
        return this.has(key);
    }

    delete(key: string): void {
        this.#values.delete(this.findKeyCaseInsensitive(key) ?? key);
    }

    keys(): IterableIterator<string> {
        return this.#values.keys();
    }

    get size(): number {
        return this.#values.size;
    }

    get count(): number {
        return this.#values.size;
    }

    entries(): IterableIterator<[string, DynamicConfig[]]> {
        return this.#values.entries();
    }

    getEnumerator(): IterableIterator<[string, DynamicConfig[]]> {
        return this.#values.entries();
    }

    [Symbol.iterator](): IterableIterator<[string, DynamicConfig[]]> {
        return this.#values[Symbol.iterator]();
    }

    forEach(callback: (value: DynamicConfig[], key: string, map: DynamicConfigMap) => void): void {
        this.#values.forEach((value, key) => callback(value, key, this));
    }

    // Resolves the stored key matching `key` case-insensitively, mirroring csharp
    // DynamicConfigMap's Dictionary(StringComparer.OrdinalIgnoreCase). Exact-match fast
    // path first; returns null when absent so set() falls back to inserting `key` as-is.
    findKeyCaseInsensitive(key: string): string | null {
        if (this.#values.has(key)) {
            return key;
        }
        const lowerKey = key.toLowerCase();
        for (const k of this.#values.keys()) {
            if (k.toLowerCase() === lowerKey) {
                return k;
            }
        }
        return null;
    }

    static parseDynamicConfig(json: JsonObject | null, configType: string): DynamicConfigMap | null {
        if (!json || json.size === 0) return null;

        const result = new DynamicConfigMap();

        for (const [componentKey, rawValue] of json.entries()) {
            if (!(rawValue instanceof JsonValue) || rawValue.typeName !== JsonValueType.Array) continue;
            const handlersArray = rawValue.asArray();
            if (!handlersArray) continue;

            const handlers: DynamicConfig[] = [];
            for (let i = 0; i < handlersArray.length; i++) {
                const handlerVal = handlersArray[i];
                if (!(handlerVal instanceof JsonValue) || handlerVal.typeName !== JsonValueType.Object) continue;
                const handlerObj = handlerVal.asObject();
                if (!handlerObj) continue;

                const config = DynamicConfig.fromJson(handlerObj, configType);

                if (config.iType) {
                    handlers.push(config);
                }
            }

            if (handlers.length > 0) {
                result.set(componentKey, handlers);
            }
        }

        return result.size > 0 ? result : null;
    }

    static mergeDynamicConfig(target: DynamicConfigMap, source: DynamicConfigMap): void {
        for (const [key, value] of source.getEnumerator()) {
            target.set(key, value);
        }
    }

    static serializeDynamicConfigToJson(config: DynamicConfigMap | null): string {
        if (!config || config.size === 0) return '{}';

        const parts: string[] = [];
        for (const [key, handlers] of config.getEnumerator()) {
            const handlerParts: string[] = [];
            for (const handler of handlers) {
                // DataSource is what identifies a PURE client binding: the client tier resolves a pure handler
                // by testing hasDataSource("Pure"). Dropping it shipped the binding with no way to recognise
                // it, so the pure resolve silently skipped the handler and the pure tier never ran. Emitted
                // ONLY when present, so DynamicViewConfig (which carries no DataSource) keeps its bytes.
                const dataSource = handler.dataSource
                    ? `,"DataSource":"${escapeJson(handler.dataSource)}"`
                    : '';
                handlerParts.push(`{"IType":"${escapeJson(handler.iType)}","IParamFile":"${escapeJson(handler.iParamFile)}"${dataSource}}`);
            }
            parts.push(`"${escapeJson(key)}":[${handlerParts.join(',')}]`);
        }

        return `{${parts.join(',')}}`;
    }
}

function escapeJson(str: string): string {
    if (!str) return '';
    return str
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
}

export const parseDynamicConfig = DynamicConfigMap.parseDynamicConfig.bind(DynamicConfigMap);
export const mergeDynamicConfig = DynamicConfigMap.mergeDynamicConfig.bind(DynamicConfigMap);
export const serializeDynamicConfigToJson = DynamicConfigMap.serializeDynamicConfigToJson.bind(DynamicConfigMap);
